import { useCallback } from 'react';
import { useState } from 'react';

interface UsePaginationParams {
  totalPages: number;
  initialPage?: number;
}

interface UsePaginationPayload {
  currentPage: number;
  currentPageIndex: number;
  onPreviousPageClick: () => void;
  onNextPageClick: () => void;
  onSpecificPageClick: (pageNumber: number) => void;
  resetPage: () => void;
}

export const usePagination = (
  params: UsePaginationParams,
): UsePaginationPayload => {
  const [currentPage, setCurrentPage] = useState<number>(
    params?.initialPage || 1,
  );

  const resetPage = useCallback(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [currentPage]);

  const onPreviousPageClick = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  const onNextPageClick = useCallback(() => {
    if (currentPage < params.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, params.totalPages]);

  const onSpecificPageClick = useCallback(
    (pageNumber: number) => {
      if (pageNumber <= params.totalPages && pageNumber >= 1) {
        setCurrentPage(pageNumber);
      }
    },
    [params.totalPages],
  );

  return {
    currentPage,
    currentPageIndex: currentPage - 1,
    onPreviousPageClick,
    onNextPageClick,
    onSpecificPageClick,
    resetPage,
  };
};
